import { useEffect, useRef } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./RangeBarsChart.css";
import { SYNTHETIC_FIELD_NEEDLES, isSyntheticField, syntheticReasonText } from "./syntheticFields";

const fmt = (n) => "£" + Math.round(n).toLocaleString("en-GB");
const fmtK = (n) => (n >= 1000 ? "£" + Math.round(n / 1000) + "k" : "£" + Math.round(n));

// Standard percentile schema for tooltip labels (P10/P40/P50/P60/P90).
// South Africa is the one confirmed shifted territory (P10/P30/P40/P50/P90) —
// see the same note on CostBandTable.jsx.
// FIXED 2026-08-05: was matching on `territory === "South Africa"` - the
// API passes through the raw DB name unchanged, and old Angular's own
// Config.territories keys that name as 'SOUTH AFRICA' (uppercase; 'South
// Africa' there is a separate, display-only `label` field never sent to
// the backend) - so this string check always failed and every territory
// silently fell back to the standard P10/P40/P50/P60/P90 labels. Matching
// on territory_id instead (2 = South Africa, per TERRITORY_SORT_ORDER)
// since numeric ids can't have a casing bug.
function percentilesFor(territoryId) {
  return territoryId === 2
    ? { min: 10, ntr: 30, exp: 40, vexp: 50, max: 90 }
    : { min: 10, ntr: 40, exp: 50, vexp: 60, max: 90 };
}

// FIXED 2026-08-05: the x-axis domain/ticks used to be a hardcoded
// [0..100000], which only ever fit placeholder/dev data. Against real DB
// figures, any territory whose Max exceeds £100k (Brazil, confirmed from
// the reported screenshot) gets plotted past x=100000 - since the SVG has
// overflow:visible (needed elsewhere for label text bleed), that excess
// draws straight past the chart panel's right edge instead of being
// clipped or rescaled, reading as "rendered off the page". Domain is now
// derived from the real data's max value, rounded up to a round step, with
// 100000 kept as a floor so small datasets still get the original spacing.
function computeXDomain(data) {
  const dataMax = Math.max(100000, ...data.map((d) => d.max || 0));
  const step = dataMax <= 200000 ? 20000 : dataMax <= 500000 ? 50000 : 100000;
  const xMax = Math.ceil(dataMax / step) * step;
  const ticks = [];
  for (let t = 0; t <= xMax; t += step) ticks.push(t);
  return { xMax, ticks };
}

// Ported from core-compensation.component.ts's sortOrder (used identically
// in CostBandTable.jsx and ComparisonChart.jsx) - fixed display order by
// territory_id (UK, Ireland, South Africa, Romania, India, Brazil). Neither
// this chart nor DistributionChart.jsx applied any sort before now, so rows
// rendered in whatever order the API happened to return them (alphabetical).
const TERRITORY_SORT_ORDER = [4, 5, 2, 3, 1, 6];

// Synthetic-field needle map / isSyntheticField / syntheticReasonText moved
// out to syntheticFields.js on 2026-08-05 (same move as distributionStats.js)
// so CostBandTable.jsx's new per-field calculator icon can reuse the exact
// same logic this chart's tooltip already used, instead of a third drifting
// copy. Verbatim extraction, no behavior changed here.

function tipHTML(d) {
  const p = percentilesFor(d.territory_id);
  const row = (label, val) =>
    `<div class="tt-row"><span class="tt-label">${label}</span><span class="tt-val">${fmt(val)}</span></div>`;
  const rowP = (label, val, pct) =>
    `<div class="tt-row"><span class="tt-label">${label} <span style="opacity:.55">P${pct}</span></span><span class="tt-val">${fmt(val)}</span></div>`;

  // FIXED 2026-08-05: was hidden whenever d.synthetic was true, replaced by
  // a single generic "Source: synthetic" line - not how the real old
  // tooltip works (compensation-range-chart.component.html). Full range EE
  // and Core band EE ALWAYS render, unconditionally; a separate "Synthetic
  // Data:" section (Base 10%/Base 90% reason text, via
  // hasSyntheticNote()/getBase10()/getBase90() there) appends below when
  // Min or Max specifically is synthetic - it's additive, never a replacement.
  const eeRows = `
    <div class="tt-row"><span class="tt-label">Full range EE</span><span class="tt-val">${d.full_range_ee_count?.toLocaleString("en-GB") ?? "-"}${d.low_n ? ' <span style="color:var(--rbc-accent);font-style:italic">*small sample</span>' : ""}</span></div>
    <div class="tt-row"><span class="tt-label">Core band EE <span style="opacity:.55">≈20%</span></span><span class="tt-val">${d.core_band_ee_count?.toLocaleString("en-GB") ?? "-"}</span></div>`;

  const base10 = syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.min);
  const base90 = syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.max);
  const syntheticRows =
    base10 || base90
      ? `<div class="tt-divider"></div>
         <div class="tt-label" style="margin-bottom:4px">Synthetic Data:</div>
         ${base10 ? `<div class="tt-row"><span class="tt-label">Base 10%</span><span class="tt-val">${base10}</span></div>` : ""}
         ${base90 ? `<div class="tt-row"><span class="tt-label">Base 90%</span><span class="tt-val">${base90}</span></div>` : ""}`
      : "";

  return `<div class="tt-title">${d.territory}</div>
    ${rowP("Min", d.min, p.min)}
    ${rowP("NTR", d.ntr, p.ntr)}
    ${rowP("Exp", d.exp, p.exp)}
    ${rowP("Vexp", d.vexp, p.vexp)}
    ${rowP("Max", d.max, p.max)}
    <div class="tt-divider"></div>
    ${row("Full range spread", d.full_range_spread)}
    ${row("Core band spread", d.core_band_spread)}
    <div class="tt-divider"></div>
    ${eeRows}
    ${syntheticRows}`;
}

const W = 1100;
const H = 460;
const M = { top: 24, right: 60, bottom: 60, left: 160 };

// Ported from compensation-range-chart.component.ts's exportExcel(). Column
// set and header styling match the old workbook. Red font is per-field
// (Market Min/NTR/Exp/Vexp/Market Max individually, matching Config.isSynthetic()
// - see isSyntheticField() above), not a whole-row reddening - now possible
// since /api/cost-band-detail returns the raw synthetic_data list alongside
// the row-level boolean. Full Range Spread / Core Band Spread / EE-count
// columns are never reddened, matching old (confirmed against the real
// workbook - only cells 4-8 ever get applyRedFont there).
async function exportExcel(data) {
  const sorted = [...data].sort(
    (a, b) => TERRITORY_SORT_ORDER.indexOf(a.territory_id) - TERRITORY_SORT_ORDER.indexOf(b.territory_id)
  );

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Compensation Overview");

  sheet.columns = [
    { header: "Role", key: "role", width: 28 },
    { header: "Territory Name", key: "territory", width: 20 },
    { header: "Job Code", key: "job_code", width: 16 },
    { header: "Market Min", key: "min", width: 16 },
    { header: "New To Role", key: "ntr", width: 16 },
    { header: "Experienced", key: "exp", width: 16 },
    { header: "V-Experienced", key: "vexp", width: 16 },
    { header: "Market Max", key: "max", width: 16 },
    { header: "Full Range Spread", key: "full_range_spread", width: 18 },
    { header: "Core Band Spread", key: "core_band_spread", width: 18 },
    { header: "Full range EE count", key: "full_range_ee_count", width: 18 },
    { header: "Core band EE count", key: "core_band_ee_count", width: 18 },
    { header: "Synthetic Data", key: "synthetic", width: 40 },
  ];

  const headerRow = sheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
  });

  const moneyCols = ["min", "ntr", "exp", "vexp", "max", "full_range_spread", "core_band_spread"];
  // Only these 5 are ever candidates for red-highlighting, same as old.
  const syntheticCheckCols = ["min", "ntr", "exp", "vexp", "max"];

  sorted.forEach((d) => {
    const row = sheet.addRow({
      role: d.role,
      territory: d.territory,
      job_code: d.job_code,
      min: d.min,
      ntr: d.ntr,
      exp: d.exp,
      vexp: d.vexp,
      max: d.max,
      full_range_spread: d.full_range_spread,
      core_band_spread: d.core_band_spread,
      full_range_ee_count: d.full_range_ee_count,
      core_band_ee_count: d.core_band_ee_count,
      synthetic: (d.synthetic_data || []).join(", "),
    });
    moneyCols.forEach((key) => {
      row.getCell(key).numFmt = "£#,##0.00";
    });
    syntheticCheckCols.forEach((key) => {
      if (isSyntheticField(d.synthetic_data, key)) {
        row.getCell(key).font = { color: { argb: "FFFF0000" }, bold: true };
      }
    });
  });

  // Merge Role and Job Code down all rows, matching the old workbook layout.
  if (sorted.length > 1) {
    sheet.mergeCells(2, 1, sorted.length + 1, 1);
    sheet.mergeCells(2, 3, sorted.length + 1, 3);
    sheet.getCell(2, 1).alignment = { vertical: "middle" };
    sheet.getCell(2, 3).alignment = { vertical: "middle" };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Compensation-Overview.xlsx");
}

// FIXED 2026-08-05 (3rd territory/quarter attempt): data/loading are now
// plain props from App.js instead of an internal fetch. Checked old code
// directly - compensation-range-chart.component.ts (the real Range Bars
// chart) has NO HTTP call of its own at all, just `@Input() territoryData`.
// This chart, CostBandTable.jsx, AND DistributionChart.jsx previously each
// independently re-fetched the same /api/cost-band-detail data keyed off
// Territory/Quarter - three redundant fetches racing each other whenever
// those reset (the real root cause of the stale-territory bug, not a
// missing guard). The one fetch now lives in JobDetailTerritoryQuarterRow.jsx
// (see App.js), matching old's loadData().
export default function RangeBarsChart({ filters, data, loading }) {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  const jobTitleId = filters?.jobTitle?.id;

  // Render the SVG imperatively, same structure as render-range-bars.js —
  // this chart is built from a lot of small animated marks per row, which
  // maps far more directly onto a single innerHTML build than onto JSX.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || data.length === 0) {
      if (svg) svg.innerHTML = "";
      return;
    }

    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;
    const rowH = innerH / data.length;
    // Fixed to match Minerva_intelligence_UI_prototype.html's .track/.full-bar/
    // .core-bar CSS exactly (was a dynamic Math.min(28, rowH*0.5) formula that
    // produced bars up to 3.5x thicker than the reference design).
    const barH = 8;
    const { xMax, ticks } = computeXDomain(data);
    const x = (v) => M.left + (v / xMax) * innerW;

    let html = "";
    ticks.forEach((t) => {
      html += `<line class="grid-line" x1="${x(t)}" x2="${x(t)}" y1="${M.top}" y2="${M.top + innerH}" />`;
      html += `<text class="axis-label" x="${x(t)}" y="${M.top + innerH + 22}" text-anchor="middle">${fmtK(t)}</text>`;
    });
    html += `<line class="axis-line" x1="${M.left}" x2="${M.left + innerW}" y1="${M.top + innerH}" y2="${M.top + innerH}"/>`;
    html += `<text class="axis-label" x="${M.left + innerW / 2}" y="${H - 8}" text-anchor="middle" style="letter-spacing:.12em">ANNUAL FULLY LOADED COST · GBP</text>`;

    data.forEach((d, i) => {
      const yCenter = M.top + rowH * i + rowH / 2;
      const yBar = yCenter - barH / 2;
      const delay = i * 80 + 100;
      const isSynthetic = d.synthetic;
      const p = percentilesFor(d.territory_id);
      const isSpecial = p.exp !== 50;
      const isLowN = d.low_n;

      html += `<g class="row-group" data-i="${i}">`;
      html += `<rect class="row-hover-target" x="${M.left}" y="${M.top + rowH * i}" width="${innerW}" height="${rowH}" />`;
      html += `<rect class="range-bar${isSynthetic ? " synthetic" : ""}" x="${x(d.min)}" y="${yBar}" width="${x(d.max) - x(d.min)}" height="${barH}" rx="4" style="animation: barIn .7s cubic-bezier(.2,.7,.2,1) ${delay}ms backwards"/>`;

      // Fixed 14px to match the prototype's .core-bar height (was barH-8,
      // which scaled off the old dynamic barH and is no longer meaningful
      // now that barH is a fixed 8px).
      const coreH = 14;
      // FIXED 2026-08-05: was gated behind `if (!isSynthetic)` - not real old
      // behavior. compensation-range-chart.component.html always renders
      // BOTH the full-range bar and the core-band bar unconditionally; only
      // the EE-count badge has a (null-only) guard: `@if(row.coreBandEE !==
      // null)`. Skipping the whole core-bar for synthetic rows is what left
      // South Africa's NTR/Exp/Vexp markers floating with no bar under them.
      html += `<rect class="core-bar" x="${x(d.ntr)}" y="${yCenter - coreH / 2}" width="${x(d.vexp) - x(d.ntr)}" height="${coreH}" rx="5" style="animation: barIn .7s cubic-bezier(.2,.7,.2,1) ${delay + 80}ms backwards"/>`;
      if (d.core_band_ee_count != null) {
        const coreNcx = (x(d.ntr) + x(d.vexp)) / 2;
        const coreNy = yCenter - coreH / 2 - 5;
        html += `<text class="core-n-label" x="${coreNcx}" y="${coreNy}" text-anchor="middle" style="opacity:0; animation: rise .6s ease ${delay + 360}ms forwards">${d.core_band_ee_count}</text>`;
      }

      html += `<text class="range-cap" x="${x(d.min) - 8}" y="${yCenter + 3.5}" text-anchor="end" style="opacity:0; animation: rise .6s ease ${delay + 200}ms forwards">${fmtK(d.min)}</text>`;
      html += `<text class="range-cap" x="${x(d.max) + 8}" y="${yCenter + 3.5}" text-anchor="start" style="opacity:0; animation: rise .6s ease ${delay + 200}ms forwards">${fmtK(d.max)}</text>`;

      html += `<circle class="tier-marker" cx="${x(d.ntr)}" cy="${yCenter}" r="5" style="transform-origin:${x(d.ntr)}px ${yCenter}px; animation: pop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 220}ms backwards"/>`;
      html += `<circle class="tier-marker" cx="${x(d.vexp)}" cy="${yCenter}" r="5" style="transform-origin:${x(d.vexp)}px ${yCenter}px; animation: pop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 260}ms backwards"/>`;
      html += `<circle class="median-marker" cx="${x(d.exp)}" cy="${yCenter}" r="6.5" style="transform-origin:${x(d.exp)}px ${yCenter}px; animation: pop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 300}ms backwards"/>`;

      // Ported from compensation-range-chart.component.ts's buildFootnote():
      // employee count is checked FIRST - the synthetic label is only a
      // fallback for rows with no real count at all. Previously this checked
      // isSynthetic first, so a row with both a real count and a (loosely
      // matched) synthetic flag always showed "synthetic · business rules"
      // instead of its real n= count - confirmed wrong against reference
      // data where every territory has a real count and none should show
      // the synthetic label.
      const subText = d.full_range_ee_count
        ? `n=${d.full_range_ee_count}${isLowN ? '<tspan fill="var(--rbc-accent)" font-style="italic" font-weight="600" dx="2" dy="-3" font-size="9">*</tspan>' : ""}${isSpecial ? " · shifted" : ""}`
        : isSynthetic
        ? `synthetic · business rules${isSpecial ? " · shifted" : ""}`
        : "";
      html += `<text class="terr-label" x="${M.left - 16}" y="${yCenter - 2}" text-anchor="end">${d.territory}</text>`;
      html += `<text class="terr-sub" x="${M.left - 16}" y="${yCenter + 14}" text-anchor="end">${subText}</text>`;
      html += `</g>`;
    });

    svg.innerHTML = html;

    svg.querySelectorAll(".row-group").forEach((g) => {
      const i = +g.dataset.i;
      g.addEventListener("mousemove", (e) => showTip(tipHTML(data[i]), e));
      g.addEventListener("mouseleave", () => hideTip());
    });

    function showTip(html, e) {
      const tt = tooltipRef.current;
      if (!tt) return;
      tt.innerHTML = html;
      tt.classList.add("on");
      const pad = 14;
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = tt.offsetWidth, h = tt.offsetHeight;
      let left = e.clientX + pad;
      let top = e.clientY + pad;
      if (left + w > vw - 8) left = e.clientX - pad - w;
      if (top + h > vh - 8) top = e.clientY - pad - h;
      tt.style.left = left + "px";
      tt.style.top = top + "px";
    }
    function hideTip() {
      tooltipRef.current?.classList.remove("on");
    }
  }, [data]);

  return (
    <section className="rbc-section">
      <div className="rbc-chart-panel">
        <div className="rbc-chart-head">
          <div>
            <div className="rbc-chart-pretitle">Annual fully loaded cost · GBP · Range bars</div>
            <div className="rbc-chart-title">Min → Max range per territory, with NTR · Exp · Vexp marked.</div>
          </div>
          {data.length > 0 && (
            <button type="button" className="rbc-export-btn" onClick={() => exportExcel(data)} title="Export this table to Excel">
              Export XLS
            </button>
          )}
        </div>

        <div className="rbc-legend">
          <div className="rbc-legend-item"><span className="rbc-swatch range"></span>Full range spread (Min → Max)</div>
          <div className="rbc-legend-item"><span className="rbc-swatch core"></span>Core band spread (NTR → Vexp)</div>
          <div className="rbc-legend-item"><span className="rbc-swatch-marker"></span>NTR / Vexp</div>
          <div className="rbc-legend-item"><span className="rbc-swatch-marker exp"></span>Experienced (Exp)</div>
          <div className="rbc-legend-item"><span className="rbc-legend-coren-swatch">104</span>Core band EE count (≈20% of full)</div>
        </div>

        {!jobTitleId && <p className="rbc-status">Select a job title to see the range bars chart.</p>}
        {jobTitleId && loading && <p className="rbc-status">Loading…</p>}
        {jobTitleId && !loading && data.length === 0 && <p className="rbc-status">No data for this selection.</p>}

        {jobTitleId && !loading && data.length > 0 && (
          <svg ref={svgRef} className="rbc-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" aria-label="Salary range bars chart" />
        )}
      </div>
      <div ref={tooltipRef} className="rbc-tooltip" role="tooltip"></div>
    </section>
  );
}
